import React, {useEffect} from 'react';
import {ThemeProvider} from '@rn-vui/themed';
import {observer} from 'mobx-react';

import ProductGrid from '../../components/ProductGrid';
import useStorePriceDisplayConditions from '../../hooks/usePriceDisplayConditions';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import rewardsTheme from '../../theme/rewardsTheme';
import {getProductDisplayRules} from '../../utils/rewards';
const MembersPriceListScreen = observer(
  ({navigation, route}: RouteNavigatorScreenProps<AppRoute.MEMBERS_PRICE_LIST>) => {
    const {external_sell_type} = route.params;

    /**
     * only allow just-for-you or members-price
     */
    useEffect(() => {
      if (!['just-for-you', 'members-price'].includes(external_sell_type)) {
        navigation.navigate(AppRoute.ROOT, {
          screen: AppRoute.MAIN,
          params: {screen: AppRoute.HOME},
        });
      }
    }, [external_sell_type, navigation]);

    /**
     * DIS-1590 review rank 3: `just-for-you` requires ENTITLEMENT, not just `hasRewards`.
     * `hasRewards` carries neither a login term nor a store term, so a lite member with
     * no session — or any member at a store that is not loyalty-enabled — reached the
     * targeted grid through this route and bypassed the gate in
     * `components/rewards/JustForYou`. MFL does not gate `member_price=private` by
     * session (measured 2026-09-02), so this gate is the only control.
     */
    const {showMemberPrice} = useStorePriceDisplayConditions();
    const isJustForYou = external_sell_type === 'just-for-you';
    const entitledToPrivate = showMemberPrice.privatePricing;

    useEffect(() => {
      if (isJustForYou && !entitledToPrivate) {
        navigation.navigate(AppRoute.ROOT, {
          screen: AppRoute.MAIN,
          params: {
            screen: AppRoute.MEMBERS_PRICE_LIST,
            params: {
              external_sell_type: 'members-price',
            },
          },
        });
      }
    }, [isJustForYou, entitledToPrivate, navigation]);

    // Decided during render, before the redirect effect can run — the private lines must
    // never be requested for an unentitled shopper.
    const member_price = isJustForYou && entitledToPrivate ? 'private' : 'public';

    const store = useStores().getLinesStore({member_price});

    const products = (store?.lines ?? []).filter(product => {
      const displayRules = getProductDisplayRules(product);
      const showProduct =
        member_price === 'public'
          ? displayRules.presentAsPublicMemberProduct
          : member_price === 'private'
            ? displayRules.presentAsPrivateMemberProduct
            : true;

      return showProduct;
    });

    return (
      <ThemeProvider theme={rewardsTheme}>
        <ProductGrid items={products} isLoading={store?.isLoading} />
      </ThemeProvider>
    );
  },
);

export default MembersPriceListScreen;
